import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Nav from "../components/Nav";
import { useAuth } from "../context/AuthContext";

// Module-level so React StrictMode remounts reuse the same in-flight consume
// instead of burning the single-use token twice.
const consumeByToken = new Map();

export default function MagicLogin() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { consumeMagicLink, completeMfa, user } = useAuth();
  const nav = useNavigate();
  const [error, setError] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Missing login token.");
      return;
    }
    let cancelled = false;
    (async () => {
      let pending = consumeByToken.get(token);
      if (!pending) {
        pending = consumeMagicLink(token);
        consumeByToken.set(token, pending);
      }
      const res = await pending;
      if (cancelled) return;
      if (!res.ok) {
        // Already logged in from the first successful consume (StrictMode / double-click).
        if (user || localStorage.getItem("cc_token")) {
          nav("/my-cards", { replace: true });
          return;
        }
        setError(res.error);
        return;
      }
      if (res.mfa_required) {
        setMfaToken(res.mfa_token);
        return;
      }
      nav("/my-cards", { replace: true });
    })();
    return () => { cancelled = true; };
    // Intentionally only re-run on token — consumeMagicLink is unstable from context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, nav]);

  const submitMfa = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await completeMfa(mfaToken, mfaCode);
    setBusy(false);
    if (!res.ok) return setError(res.error);
    nav("/my-cards", { replace: true });
  };

  return (
    <div>
      <Nav />
      <section className="border-b border-black bg-[color:var(--cc-bg-2)]">
        <div className="max-w-md mx-auto px-4 py-16">
          {mfaToken ? (
            <form onSubmit={submitMfa} className="space-y-4 bg-white border border-black p-6">
              <h1 className="font-display font-black text-3xl tracking-tighter">Confirm it's you</h1>
              <p className="text-sm text-neutral-700">Enter your authenticator or backup code to finish signing in.</p>
              <input
                className="w-full px-4 py-3 border border-black outline-none font-mono-cc tracking-widest"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                required
                autoFocus
              />
              {error && <div className="label-tag text-red-600">{error}</div>}
              <button type="submit" disabled={busy} className="w-full bg-black text-white label-tag px-4 py-4 brutal-btn disabled:opacity-60">
                {busy ? "Verifying…" : "Sign in"}
              </button>
            </form>
          ) : (
            <div className="text-center">
              <h1 className="font-display font-black text-3xl tracking-tighter">
                {error ? "Link invalid" : "Signing you in…"}
              </h1>
              {error && (
                <>
                  <p className="mt-3 text-red-600">{error}</p>
                  <Link to="/cardholder/login" className="mt-6 inline-block label-tag underline">Back to login</Link>
                </>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
